import type { LiveVoiceSession } from '@/lib/live-voice-session';
import type { LiveVoiceUsageReport } from '@/lib/live-voice-usage';
import type { MutableRefObject } from 'react';
import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
} from 'convex/server';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import type { RealtimeVoiceDelegationTarget } from '@/lib/realtime-voice-agent';
import { createRealtimeVoiceRuntime } from '@/components/org-chat/create-realtime-voice-runtime';
import { createRealtimeVoiceTools } from './realtime-voice-tools';
import type { RealtimeVoiceToolContext } from './realtime-voice-tool-context';
import type { useRealtimeResponseController } from '@/components/org-chat/use-realtime-response-controller';
import type { useRealtimeQuestionnairePrompts } from '@/components/org-chat/use-realtime-questionnaire-prompts';
import type { useVoiceCallUiController } from '@/components/org-chat/use-voice-call-ui-controller';
import type { useVoiceDelegationReceipts } from '@/components/org-chat/use-voice-delegation-receipts';
import {
  type VoiceRecordingSessionState,
  type useVoiceRecordingController,
} from '@/components/org-chat/use-voice-recording-controller';
import {
  addBoundedRealtimeSetItem,
  classifyRealtimeVoiceFailure,
  withRealtimeConnectTimeout,
  type RealtimeVoiceFailureKind,
} from '@/components/org-chat/realtime-voice-lifecycle';
import { getRealtimeVoiceFailureMessage } from '@/components/org-chat/voice-call-runtime-errors';
import { getRealtimeVoiceOpeningGreetingInstruction } from '@/i18n/realtime-voice-greeting-copy';
import type { getAskQuestionCopy } from '@/i18n/ask-question-copy';
import type { getRealtimeVoiceCallCopy } from '@/i18n/realtime-voice-call-copy';
import { getActiveRealtimeVoiceQuestionnaire } from '@/lib/realtime-voice-questionnaire';
import { VOICE_RECORDING_RETENTION } from '@/lib/realtime-voice-recording-config';
import { buildRealtimeVoiceProviderConnectOptions } from '@/components/org-chat/realtime-voice-provider-connection';
import { getRealtimeLeaseRemainingDurationMs } from '@/components/org-chat/realtime-voice-lease-activity';

const CONNECT_TIMEOUT_MS = 20_000;
const MICROPHONE_TIMEOUT_MS = 30_000;
const MAX_GREETED_SESSIONS = 100;
const greetedSessionIds = new Set<string>();

type ActionCall<Reference extends FunctionReference<'action'>> = (
  args: FunctionArgs<Reference>
) => Promise<FunctionReturnType<Reference>>;
type MutationCall<Reference extends FunctionReference<'mutation'>> = (
  args: FunctionArgs<Reference>
) => Promise<FunctionReturnType<Reference>>;

type RuntimeRefs = {
  mountedRef: MutableRefObject<boolean>;
  sourceChatVisibleRef: MutableRefObject<boolean>;
  delegationTargetRef: MutableRefObject<RealtimeVoiceDelegationTarget>;
  sessionRef: MutableRefObject<LiveVoiceSession | null>;
  leaseSessionIdRef: MutableRefObject<string | null>;
  lastStatusEventIdRef: MutableRefObject<string | null>;
  trackedUsageEventsRef: MutableRefObject<Set<string>>;
  pendingUsageReportsRef: MutableRefObject<Set<Promise<void>>>;
};

type Actions = {
  createSession: ActionCall<typeof api.voice.realtime.createRealtimeSession>;
  reportLiveUsage: (
    args: LiveVoiceUsageReport & {
      orgThreadId: Id<'orgThreads'>;
      sessionId: string;
    }
  ) => Promise<unknown>;
  heartbeatLease: MutationCall<
    typeof api.voice.realtime_provider_state.heartbeatRealtimeVoiceSession
  >;
  endLease: MutationCall<
    typeof api.products.ai.lib.voice_agent_bridge_queries.endRealtimeVoiceSession
  >;
  getChatContext: ActionCall<typeof api.voice.realtime.getRealtimeChatContext>;
  sendChatMessage: ActionCall<typeof api.voice.realtime_thread_messaging.send>;
  getOffPageQuestion: ActionCall<
    typeof api.voice.realtime_pending_question.getOffPageQuestion
  >;
  runNativeWebSearch: ActionCall<typeof api.voice.realtime.runNativeWebSearch>;
  runQuickResearch: ActionCall<typeof api.voice.realtime.runQuickResearch>;
  answerAskQuestion: ActionCall<
    typeof api.products.ai.actions.answer_ask_question.answerAskQuestion
  >;
  answerAskQuestionGroup: ActionCall<
    typeof api.products.ai.actions.answer_ask_question.answerAskQuestionGroup
  >;
};

export type StartRealtimeVoiceCallArgs = {
  orgThreadId: Id<'orgThreads'>;
  sessionId: string;
  locale: string;
  askQuestionCopy: ReturnType<typeof getAskQuestionCopy>;
  voiceCopy: ReturnType<typeof getRealtimeVoiceCallCopy>;
  abortController: AbortController;
  isCurrentCall: () => boolean;
  refs: RuntimeRefs;
  actions: Actions;
  navigationActions: Pick<
    RealtimeVoiceToolContext,
    'findNavigationTargets' | 'navigateUi' | 'deliverNavigation'
  >;
  recording: ReturnType<typeof useVoiceRecordingController>;
  responses: ReturnType<typeof useRealtimeResponseController>;
  questionnaire: ReturnType<typeof useRealtimeQuestionnairePrompts>;
  ui: ReturnType<typeof useVoiceCallUiController>;
  receipts: ReturnType<typeof useVoiceDelegationReceipts>;
  endActiveLease: () => void;
  scheduleLeaseExpiry: (leaseDurationMs: number, onExpiry: () => void) => void;
  onLeaseStarted: () => void;
  onConnected: () => void;
};

async function cleanupStaleStart(
  recording: StartRealtimeVoiceCallArgs['recording'],
  mediaStream?: MediaStream | null,
  audioElement?: HTMLAudioElement | null,
  recordingSession?: VoiceRecordingSessionState | null
) {
  mediaStream?.getTracks().forEach((track) => track.stop());
  if (audioElement) audioElement.srcObject = null;
  if (!recordingSession) return;
  await recording
    .endSession({
      voiceSessionId: recordingSession.voiceSessionId,
      endedAt: Date.now(),
    })
    .catch((error) => {
      console.warn('[voice-call] failed to end stale recording session', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
    });
}

function endStaleLease(
  args: StartRealtimeVoiceCallArgs,
  leaseSessionId: string
) {
  void args.actions
    .endLease({ orgThreadId: args.orgThreadId, sessionId: leaseSessionId })
    .catch((error) => {
      console.warn('[voice-call] failed to end stale realtime lease', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
    });
}

export async function startRealtimeVoiceCall(
  args: StartRealtimeVoiceCallArgs
): Promise<void> {
  let realtimeSession: LiveVoiceSession | null = null;
  let createdLeaseSessionId: string | null = null;
  let micStream: MediaStream | null = null;
  let assistantAudioElement: HTMLAudioElement | null = null;
  let localRecordingSession: VoiceRecordingSessionState | null = null;
  try {
    const mediaRequest = navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
        autoGainControl: false,
      },
    });
    micStream = await withRealtimeConnectTimeout(mediaRequest, {
      timeoutMs: MICROPHONE_TIMEOUT_MS,
      onTimeout: () => {
        void mediaRequest
          .then((lateStream) => {
            lateStream.getTracks().forEach((track) => track.stop());
          })
          .catch(() => {});
      },
    });
    micStream.getAudioTracks().forEach((track) => {
      track.enabled = false;
    });
    if (!args.isCurrentCall()) {
      await cleanupStaleStart(args.recording, micStream);
      return;
    }
    args.recording.micStreamRef.current = micStream;

    assistantAudioElement = document.createElement('audio');
    assistantAudioElement.autoplay = true;
    if (!args.isCurrentCall()) {
      await cleanupStaleStart(args.recording, micStream, assistantAudioElement);
      return;
    }
    args.recording.audioElementRef.current = assistantAudioElement;

    const credentialRequestStartedAt = performance.now();
    const credential = await args.actions.createSession({
      orgThreadId: args.orgThreadId,
      sessionId: args.sessionId,
      protocol: 'live',
      locale: args.locale,
    });
    createdLeaseSessionId = credential.leaseSessionId;
    if (!args.isCurrentCall()) {
      endStaleLease(args, credential.leaseSessionId);
      await cleanupStaleStart(
        args.recording,
        micStream,
        assistantAudioElement,
        localRecordingSession
      );
      return;
    }
    args.refs.leaseSessionIdRef.current = credential.leaseSessionId;
    args.onLeaseStarted();

    if (args.recording.enabled) {
      try {
        const recordingSession = await args.recording.startSession({
          orgThreadId: args.orgThreadId,
          realtimeSessionId: credential.leaseSessionId,
          recordUserAudio: args.recording.recordUserAudio,
          recordAssistantAudio: args.recording.recordAssistantAudio,
          retentionPolicy: VOICE_RECORDING_RETENTION,
        });
        if (recordingSession.voiceSessionId) {
          localRecordingSession = {
            voiceSessionId: recordingSession.voiceSessionId,
            orgSlug: recordingSession.orgSlug,
          };
          if (!args.isCurrentCall()) {
            if (
              args.refs.leaseSessionIdRef.current === credential.leaseSessionId
            ) {
              args.refs.leaseSessionIdRef.current = null;
            }
            endStaleLease(args, credential.leaseSessionId);
            await cleanupStaleStart(
              args.recording,
              micStream,
              assistantAudioElement,
              localRecordingSession
            );
            return;
          }
          args.recording.sessionRef.current = localRecordingSession;
        }
      } catch (recordingError) {
        console.warn(
          '[voice-call] voice recording session could not be started',
          {
            errorName:
              recordingError instanceof Error
                ? recordingError.name
                : 'UnknownError',
          }
        );
      }
    }
    if (!args.isCurrentCall()) {
      if (args.refs.leaseSessionIdRef.current === credential.leaseSessionId) {
        args.refs.leaseSessionIdRef.current = null;
      }
      endStaleLease(args, credential.leaseSessionId);
      await cleanupStaleStart(
        args.recording,
        micStream,
        assistantAudioElement,
        localRecordingSession
      );
      return;
    }

    const tools = createRealtimeVoiceTools(args, credential.leaseSessionId);

    const terminateFailedCall = (
      failure: RealtimeVoiceFailureKind,
      diagnostic?: unknown
    ) => {
      if (!args.isCurrentCall()) return;
      args.abortController.abort();
      realtimeSession?.close();
      if (args.refs.sessionRef.current === realtimeSession) {
        args.refs.sessionRef.current = null;
      }
      args.recording.finishSession();
      args.ui.callConnectedAtRef.current = null;
      args.ui.micUnmutedAtRef.current = null;
      args.responses.reset();
      args.endActiveLease();
      args.ui.setIsCapturing(false);
      args.ui.setPttActive(false);
      args.ui.setStatus('error');
      args.ui.setErrorText(
        getRealtimeVoiceFailureMessage(args.voiceCopy, failure)
      );
      if (diagnostic !== undefined) {
        console.warn('[voice-call] realtime call terminated', {
          failure,
          diagnostic,
        });
      }
    };

    const runtime = createRealtimeVoiceRuntime({
      args,
      tools,
      credential,
      terminateFailedCall,
      micStream,
      assistantAudioElement,
      orgThreadId: args.orgThreadId,
      isCurrentCall: args.isCurrentCall,
      onRemoteStream: (remoteStream) => {
        args.recording.remoteStreamRef.current = remoteStream;
        if (args.recording.assistantRecordingActiveRef.current) {
          args.recording.startTurn('assistant', remoteStream);
        }
      },
    });
    realtimeSession = runtime.session;
    if (!args.isCurrentCall()) {
      realtimeSession?.close();
      endStaleLease(args, credential.leaseSessionId);
      await cleanupStaleStart(
        args.recording,
        micStream,
        assistantAudioElement,
        localRecordingSession
      );
      return;
    }
    args.refs.sessionRef.current = realtimeSession;
    args.ui.applyMuteState();
    const initialLeaseDurationMs = getRealtimeLeaseRemainingDurationMs({
      leaseDurationMs: credential.leaseDurationMs,
      requestStartedAt: credentialRequestStartedAt,
      now: performance.now(),
    });
    args.scheduleLeaseExpiry(initialLeaseDurationMs, () => {
      terminateFailedCall('connection', { reason: 'lease_expired' });
    });
    await withRealtimeConnectTimeout(
      realtimeSession.connect(
        buildRealtimeVoiceProviderConnectOptions(credential)
      ),
      {
        timeoutMs: CONNECT_TIMEOUT_MS,
        onTimeout: () => realtimeSession?.close(),
      }
    );
    if (!args.isCurrentCall()) {
      realtimeSession?.close();
      await cleanupStaleStart(
        args.recording,
        micStream,
        assistantAudioElement,
        localRecordingSession
      );
      return;
    }

    args.ui.applyMuteState();
    args.ui.callConnectedAtRef.current = Date.now();
    args.ui.setCallElapsedMs(0);
    args.ui.setStatus('listening');
    args.onConnected();

    const activeQuestionnaire = getActiveRealtimeVoiceQuestionnaire(
      String(args.orgThreadId)
    );
    if (activeQuestionnaire) {
      args.questionnaire.lastToolCallIdRef.current =
        activeQuestionnaire.toolCallId;
      args.questionnaire.sendStepPrompt(
        activeQuestionnaire,
        `initial:${activeQuestionnaire.promptKey}`
      );
    }
    if (!greetedSessionIds.has(args.sessionId) && !activeQuestionnaire) {
      addBoundedRealtimeSetItem(
        greetedSessionIds,
        args.sessionId,
        MAX_GREETED_SESSIONS
      );
      args.responses.requestResponseWithTicket(
        {
          kind: 'opening_greeting',
          sessionId: args.sessionId,
          reason: 'call_opened',
        },
        {
          instructions: getRealtimeVoiceOpeningGreetingInstruction(args.locale),
          tool_choice: 'none',
        }
      );
    }
  } catch (error) {
    console.error('[voice-call] Live start failed', {
      errorName: error instanceof Error ? error.name : 'UnknownError',
    });
    realtimeSession?.close();
    if (!args.isCurrentCall()) {
      if (createdLeaseSessionId) endStaleLease(args, createdLeaseSessionId);
      await cleanupStaleStart(
        args.recording,
        micStream,
        assistantAudioElement,
        localRecordingSession
      );
      return;
    }
    args.abortController.abort();
    if (args.refs.sessionRef.current === realtimeSession)
      args.refs.sessionRef.current = null;
    args.recording.finishSession();
    args.responses.reset();
    args.endActiveLease();
    args.ui.setIsCapturing(false);
    args.ui.setPttActive(false);
    args.ui.setStatus('error');
    args.ui.setErrorText(
      getRealtimeVoiceFailureMessage(
        args.voiceCopy,
        classifyRealtimeVoiceFailure(error)
      )
    );
  }
}
